/**
 * Copyright IBM Corp. 2020, 2025
 *
 * This source code is licensed under the Apache-2.0 license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React__default from 'react';
import { p as propTypesExports } from '../../_virtual/index.js';
import { pkg } from '../../settings.js';
import { Link } from '@carbon/react';

// The block part of our conventional BEM class names (blockClass__E--M).
const blockClass = `${pkg.prefix}--http-errors`;
const componentName = 'HTTPErrorContent';
let HTTPErrorContent = _ref => {
  let {
    description,
    errorCodeLabel,
    title,
    links
  } = _ref;
  return /*#__PURE__*/React__default.createElement("div", {
    className: `${blockClass}__content`
  }, errorCodeLabel && /*#__PURE__*/React__default.createElement("p", {
    className: `${blockClass}__error-code-label`
  }, errorCodeLabel), title && /*#__PURE__*/React__default.createElement("h1", {
    className: `${blockClass}__title`
  }, title), description && /*#__PURE__*/React__default.createElement("p", {
    className: `${blockClass}__description`
  }, description), links && links.length && links.map(link => /*#__PURE__*/React__default.createElement(Link, _extends({}, link, {
    role: "link",
    href: link.href,
    key: link.text,
    className: `${blockClass}__link`
  }), link.text)));
};

// The display name of the component, used by React. Note that displayName
// is used in preference to relying on function.name.
HTTPErrorContent.displayName = componentName;

// The types and DocGen commentary for the component props,
// in alphabetical order (for consistency).
// See https://www.npmjs.com/package/prop-types#usage.
HTTPErrorContent.propTypes = {
  /**
   * String that will provide the description for the HTTP error code
   */
  description: propTypesExports.string.isRequired,
  /**
   * String that will describe the error that occurred
   */
  errorCodeLabel: propTypesExports.string.isRequired,
  /**
   * Links that will display for extra context when receiving particular errors
   */
  links: propTypesExports.arrayOf(propTypesExports.shape({
    ...Link.propTypes,
    /**
     * The text to display for the link
     */
    text: propTypesExports.string.isRequired,
    /**
     * The link's destination
     */
    href: propTypesExports.string.isRequired
  })),
  /**
   * This will be for the main title of the HTTP error component
   */
  title: propTypesExports.string.isRequired
};

export { HTTPErrorContent };
